import { apiGet, apiPost, apiPatch, apiDelete, API_BASE_URL, getBaseAuthHeaders } from './client';

// ===== Types =====

// Phase 109 (D109-04, D109-06) — wire contract mirrored from the backend
// union in backend/src/services/assignmentRateResolverService.ts. The
// frontend cannot import from the backend, so this comment is the source of
// truth to keep in sync.
export type RateCoverage = 'priced' | 'explicit_zero' | 'no_rate';

export interface InvoiceUserBreakdown {
  userId: number;
  userName: string;
  hours: number;
  billingRate: number;
  totalBilling: number;
  // Phase 109 (D109-04, D109-06) — every field below is OPTIONAL even though
  // the backend interface declares `rateCoverage` required. `Invoice.data` is
  // a frozen JSON snapshot: every invoice issued before this phase has none
  // of these properties on its users[] rows. Typing `rateCoverage` as
  // required would be a lie that pushes the render into the strict-falsy
  // trap — render on `=== 'no_rate'` / `=== 'explicit_zero'`, never on a
  // falsy test, so an absent field (historical snapshot) matches neither and
  // renders exactly as it did before this phase.
  rateCoverage?: RateCoverage;
  sourceRate?: number;
  sourceCurrency?: string;
  mixedRates?: boolean;
}

/**
 * UAT 2026-05-13 / QO0 — `kind` discriminates between the billing modes:
 *   - 'hourly': legacy entries × billingRate; `users[]` populated
 *   - 'monthly_package': SLA Balíček paushal × ČNB FX to CZK; `paushalAmount`,
 *     `paushalCurrency`, `fxRate`, `fxRateDate`, `packageHours` populated
 *   - 'fixed_price' (QO0, billable): contractAmount × ČNB FX → CZK; `contractAmount`
 *     (string) + `contractCurrency` carried for display/audit; `fxRate` + `fxRateDate`
 *     for transparency; `totalBilling` = CZK billed amount (non-zero).
 *   - 'fixed_price_excluded': legacy/historical only (Phase 58 D-02, reversed by QO0);
 *     `contractAmount` + `contractCurrency` populated; `totalBilling` = 0.
 *   - 'time_and_material': Phase 58 — per-role hourly billing; `byRole[]` populated;
 *     `hasZeroRate` signals at least one role has no rate configured
 * All modes expose `totalBilling` in CZK (Invoice ledger currency).
 */
export type InvoiceProjectKind =
  | 'hourly'
  | 'monthly_package'
  | 'fixed_price'
  | 'fixed_price_excluded'
  | 'time_and_material';

export interface InvoiceOverageRoleRow {
  roleId: number | null;
  roleName: string | null;
  hours: number;
  hourlyRate: number;
  billing: number;
  hasRate: boolean;
}

// MKS-552 — one month of overage, mirroring the backend `InvoiceOverageMonth`
// (backend/src/services/invoiceService.ts). The invoice month comes first
// (`isPriorMonth: false`); every later element is an earlier month whose own
// invoice already shipped without its overage. `included: false` means the
// month is shown but NOT billed here — it blocks only itself.
export interface InvoiceOverageMonth {
  period: string; // YYYY-MM
  isPriorMonth: boolean;
  included: boolean;
  notIncludedReason?: 'awaiting_approval' | 'partial_approval' | 'missing_rate';
  hours: number;
  billing: number;
  byRole: InvoiceOverageRoleRow[];
  approval: {
    approvedHours: number | null;
    status: 'missing' | 'less' | 'ok';
  } | null;
  hasZeroRate: boolean;
}

export interface InvoiceOverageBreakdown {
  totalHours: number;
  totalBilling: number;
  byRole: InvoiceOverageRoleRow[];
  approval: {
    approvedHours: number | null;
    status: 'missing' | 'less' | 'ok';
  } | null;
  hasZeroRate: boolean;
  // MKS-552 — OPTIONAL, like every other snapshot-era field here: absent with
  // the release flag off and on every invoice issued before it, and the
  // renderer then draws the single block it always has. Never test it for
  // truthiness alone — an empty array is "no months", not "legacy snapshot".
  byMonth?: InvoiceOverageMonth[];
}

// Phase 110 (Plan 110-06, D-07, D-12) — mirrors the backend
// `InvoiceProjectDiscount` interface (backend/src/services/invoiceService.ts)
// field for field. `mode` is never 'none': a project with no discount
// configured, or one whose applied percentage resolves to 0, omits the
// `discount` key on its breakdown row entirely rather than emitting a zero
// (REQ-110-07) — the frontend must treat absence, not a falsy check, as the
// "no discount" signal.
export interface InvoiceProjectDiscount {
  mode: 'tiers' | 'manual';
  percentage: number; // the APPLIED percentage, after any override
  configuredPercentage: number | null; // what the configuration alone would have resolved to
  overridden: boolean;
  tierMinHours: number | null;
  hourCount: number;
  retainerInBase: boolean;
  baseCurrency: string; // a single bucket's currency, or the literal 'MIXED'
  baseSourceAmount: number | null; // null when baseCurrency is 'MIXED'
  baseAmountCzk: number;
  amountCzk: number;
}

// MKS-206 (110-REVIEW WR-02) — mirrors the backend
// `InvoiceProjectDiscountAvailable`. Emitted INSTEAD of `discount`, never
// beside it, on a row whose configured discount gave nothing away this period
// (a tiers project below its first threshold, a tiers project with no tier
// rows, a manual 0%). The backend accepts a one-off override on such a row;
// this is how the page learns the row exists to be overridden.
export interface InvoiceProjectDiscountAvailable {
  mode: 'tiers' | 'manual';
  configuredPercentage: number; // 0 in the common case
  tierMinHours: number | null;
  hourCount: number;
}

export interface InvoiceProjectBreakdown {
  projectId: number;
  projectName: string;
  kind: InvoiceProjectKind;
  totalHours: number;
  totalBilling: number;
  // Phase 110 (Plan 110-06) — present only when a discount actually applied
  // to this row. Read every figure from this block, never derive one from
  // `totalBilling`/`entry.totalBilling`/any timesheet aggregate.
  discount?: InvoiceProjectDiscount;
  // MKS-206 — mutually exclusive with `discount` above; absent on a project
  // with no discount configuration at all.
  discountAvailable?: InvoiceProjectDiscountAvailable;
  users?: InvoiceUserBreakdown[];
  // SLA Balíček (monthly_package) fields:
  paushalAmount?: number;
  paushalCurrency?: string;
  fxRate?: number;
  fxRateDate?: string;
  packageHours?: number;
  // B2c (2026-05-15) — overage addendum, present when the package was
  // exceeded in the invoice period and overLimitAllowed=true.
  overage?: InvoiceOverageBreakdown;
  // Phase 53 (Plan 53-04 / COVERAGE-08 display) — hours carried forward from
  // prior period rollover. Present only when > 0; absent on historical invoices.
  carryoverHours?: number;
  // Phase 58 (D-02) — Fixed Price excluded line fields.
  // contractAmount is a string (DECIMAL(19,4) serialized via .toFixed(4)) — NOT a number.
  contractAmount?: string | null;
  contractCurrency?: string | null;
  // Phase 58 (PAY-TM-03) — Time & Material per-role breakdown.
  // byRole/hasZeroRate live at the top level of the breakdown item (not nested
  // inside overage) — matching the backend schema (invoice.ts lines 50-64).
  byRole?: {
    roleId: number | null;
    roleName: string | null;
    hours: number;
    hourlyRate: number;
    billing: number;
    hasRate: boolean;
  }[];
  hasZeroRate?: boolean;
  // Phase 109 (D109-04) — deliberately-non-billable counterpart to
  // hasZeroRate. `hasZeroRate` is the blocking signal (any user resolved
  // `no_rate`); `hasExplicitZeroRows` is informational only (at least one
  // user is deliberately billed 0) and does NOT block generation. Optional
  // for the same reason as InvoiceUserBreakdown's new fields — historical
  // snapshots never carry it.
  hasExplicitZeroRows?: boolean;
  // Quick task 260902-eg2 (FX-DISPLAY, D-01) — neutral source-currency
  // metadata on time_and_material / hourly rows, mirroring the backend
  // interface exactly. D-01 fallback chain used by the renderer:
  //   currency: sourceCurrency ?? paushalCurrency ?? contractCurrency ?? ''
  //   source amount: sourceAmount ?? paushalAmount ?? (contractAmount != null ? parseFloat(contractAmount) : undefined)
  // D-04 — mixedSourceCurrencies is set INSTEAD of sourceCurrency/sourceAmount/
  // fxRate/fxRateDate when an hourly project's in-period entries resolve to
  // 2+ distinct source currencies; the renderer shows a note, not a rate.
  sourceCurrency?: string;
  sourceAmount?: number;
  mixedSourceCurrencies?: string[];
  // Quick task 260912-j8z (G-01) — expense receipts contributing
  // billedAmount to this row's total. Populated since Phase 62 (TBI-04)
  // but only reachable by the client now that the backend response schema
  // declares it. `expenseSheetId` is optional (not merely nullable)
  // because a snapshot generated before this task carries the array
  // without the field at all; the renderer degrades to plain text when
  // it is missing or null.
  expenses?: {
    receiptId: number;
    expenseSheetId?: number | null;
    description?: string | null;
    receiptDate?: string | null;
    amount: number;
    billedAmount: number;
  }[];
}

export interface InvoiceBreakdown {
  byProject: InvoiceProjectBreakdown[];
  totalHours: number;
  totalBilling: number;
  // Phase 110 (Plan 110-06, REQ-110-07) — present only when at least one
  // project row above carries a discount. `totalBilling` above is already
  // NET; `grossTotal` reconstructs what the total would have been before any
  // discount, `discountTotal` is their difference.
  discountTotal?: number;
  grossTotal?: number;
  // MKS-214 — PREVIEW ONLY: the due date this invoice would get if issued
  // today (issue date + the company payment term), used to pre-fill the
  // generate form's Due Date field. Absent on a generate response, on a stored
  // `Invoice.data` snapshot, and while the release flag is off — so treat
  // absence, not a falsy check, as "no suggestion".
  defaultDueDate?: string;
}

export interface Invoice {
  id: number;
  name: string | null;
  invoiceNumber: string | null;
  orderNumber: string | null;
  from: string | null;
  to: string | null;
  invoicedAmount: number;
  // Phase 110 (Plan 110-06, D-10, D-11) — `invoicedAmount` above is NET (what
  // the client pays); `preDiscountGross` is the gross before any discount,
  // ALWAYS populated (equal to `invoicedAmount` on an undiscounted invoice);
  // `discountAmount` is their difference (0 when undiscounted).
  discountAmount?: number;
  preDiscountGross?: number;
  // MKS-214 — due date + manual payment marking. `status` is derived by the
  // backend (`invoicePaymentStatus` in invoiceService.ts) on every read, so an
  // invoice that falls overdue overnight reports it without anything having
  // written to it. Never re-derive it here from `dueDate`: the two would drift
  // across a timezone boundary.
  dueDate: string | null;
  // MKS-454 — the date the server decided `status` from. Equal to `dueDate`
  // when there is one; when there is not it is DERIVED (issue date + the
  // payment term of the invoice's projects, fallback Net 14) and
  // `derivedNetDays` carries the term so the UI can mark it as derived. Both
  // are null while `mks_454_derived_invoice_due_date` is off. The derived date
  // is never stored: `dueDate` above stays null, and PATCHing it back is still
  // the only way an invoice gets a real one.
  effectiveDueDate: string | null;
  derivedNetDays: number | null;
  paidAt: string | null;
  paidById: number | null;
  status: InvoicePaymentStatus;
  // MKS-352 — soft delete. All four are null on a live invoice, so treat a
  // non-null `deletedAt` (never a falsy check on the others — a reason can be
  // any string and a name can be missing) as "this invoice was cancelled":
  // render it read-only with the banner and offer no mutating control.
  // `deletedAt` is a full ISO timestamp, not a date — the log shows the time
  // of day. `deletedByName` is resolved by the server; `deletedById` alone is
  // a bare id the SPA has nothing to join against.
  deletedAt: string | null;
  deletedById: number | null;
  deletedByName: string | null;
  deleteReason: string | null;
  projectIds: number[];
  projects: { id: number; name: string; fullName: string | null }[];
  data: InvoiceBreakdown | null;
  createdAt: string;
}

// MKS-214 — mirrors the backend union in invoiceService.ts. 'unpaid' covers
// both "no due date at all" (every invoice issued before MKS-214) and "due,
// but not yet past".
export type InvoicePaymentStatus = 'unpaid' | 'overdue' | 'paid';

// Phase 62 (Plan 62-06) — selective billing: include only ticked components.
// undefined = include all (backwards-compatible with existing cron + bulk flows).
export interface InclusionDescriptor {
  package?: boolean;
  overage?: boolean;
  expenseReceiptIds?: number[];
  entryIds?: number[];
}

export interface GenerateInvoiceInput {
  projectIds: number[];
  from: string;
  to: string;
  name?: string;
  invoiceNumber?: string;
  orderNumber?: string;
  inclusion?: InclusionDescriptor;
  // Phase 110 (Plan 110-06, D-07) — a one-off, per-project percentage
  // override entered at generation time, keyed by project id as a string.
  // Sparse: only projects with an entered value are included. The value
  // REPLACES the configured tier/manual percentage for this invoice alone —
  // it never stacks on top of it. This single `discountOverrides` field is
  // sent on both the preview and the generate request, since both share this
  // input type.
  discountOverrides?: Record<string, string>;
  // MKS-214 — due date finance entered at issue time (`YYYY-MM-DD`).
  dueDate?: string;
  /**
   * MKS-136 — the billing periods this invoice is for. Generation links them
   * to the created invoice and flips them to `invoiced`; a Fixed Price
   * period's amount also pre-fills that project's draw-down, so without this
   * a four-phase contract would bill all of itself on phase one's invoice.
   * Sent on both the preview and the generate request, since both share this
   * input type — the preview must show exactly what generation will draw.
   */
  billingPeriodIds?: number[];
}

// ===== Invoice API =====

export async function getInvoices(filters?: {
  projectId?: number;
  periodId?: number;
  status?: InvoicePaymentStatus;
  /**
   * MKS-352 — `true` asks for the deletion log instead of the live list. The
   * two are complements: a cancelled invoice never appears in the live list
   * and a live one never appears in the log.
   */
  deleted?: boolean;
}): Promise<Invoice[]> {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set('projectId', String(filters.projectId));
  if (filters?.periodId) params.set('periodId', String(filters.periodId));
  if (filters?.status) params.set('status', filters.status);
  if (filters?.deleted) params.set('deleted', 'true');
  const query = params.toString();
  const res = await apiGet<{ data: Invoice[] }>(`/invoices${query ? `?${query}` : ''}`);
  return res.data;
}

/**
 * MKS-329 — one row per customer for the dashboard's "Outstanding invoices"
 * card. Mirrors `OutstandingCustomerRow` in
 * `backend/src/services/outstandingInvoicesService.ts`.
 *
 * `overdueTotal` is the part of `outstandingTotal` that is past its due date,
 * never a separate sum on top of it. `currency` comes from the server rather
 * than being assumed here — the card used to hard-code CZK.
 */
export interface OutstandingCustomerRow {
  key: string;
  customerId: number | null;
  customerName: string;
  outstandingTotal: number;
  overdueTotal: number;
  invoiceCount: number;
  /** `YYYY-MM-DD`, or null when none of the customer's unpaid invoices has a due date. */
  oldestDueDate: string | null;
  maxDaysOverdue: number;
  currency: string;
}

/**
 * MKS-329 — the aggregation the dashboard used to do in the browser over every
 * invoice in the system.
 */
export async function getOutstandingInvoices(): Promise<OutstandingCustomerRow[]> {
  const res = await apiGet<{ data: OutstandingCustomerRow[] }>('/invoices/outstanding');
  return res.data;
}

export async function getInvoice(id: number): Promise<Invoice> {
  const res = await apiGet<{ data: Invoice }>(`/invoices/${id}`);
  return res.data;
}

export async function previewInvoice(input: GenerateInvoiceInput): Promise<InvoiceBreakdown> {
  const res = await apiPost<{ data: InvoiceBreakdown }>('/invoices/preview', input);
  return res.data;
}

export async function generateInvoice(input: GenerateInvoiceInput): Promise<{ invoice: Invoice; breakdown: InvoiceBreakdown }> {
  const res = await apiPost<{ data: { invoice: Invoice; breakdown: InvoiceBreakdown } }>('/invoices', input);
  return res.data;
}

export interface UpdateInvoiceInput {
  name?: string;
  invoiceNumber?: string;
  orderNumber?: string;
  // MKS-214 — `null` clears the due date; omitting the key leaves it alone.
  dueDate?: string | null;
}

export async function updateInvoice(id: number, input: UpdateInvoiceInput): Promise<Invoice> {
  const res = await apiPatch<{ data: Invoice }>(`/invoices/${id}`, input);
  return res.data;
}

/**
 * MKS-352 — cancel an invoice, with the reason finance typed into the delete
 * dialog. The reason is mandatory: the server 400s without one.
 */
export async function deleteInvoice(id: number, reason: string): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/invoices/${id}`, { reason });
}

/**
 * MKS-352 — one invoice's audit trail: created, metadata and due-date edits,
 * paid/unpaid, the generation-time discount overrides and the cancellation
 * with its reason.
 *
 * Mirrors the generic `getAuditTrail` response shape so the same
 * `<StateHistory>` renders either — unlike that one, this endpoint is open to
 * finance.
 */
export interface InvoiceHistoryEntry {
  id: number;
  subjectType: string;
  subjectId: number;
  state: string | null;
  reason: string | null;
  changes: { field: string; oldValue: unknown; newValue: unknown }[] | Record<string, unknown> | null;
  createdAt: string | null;
  user: { id: number; email: string; firstName: string | null; lastName: string | null } | null;
}

export async function getInvoiceHistory(
  id: number,
  limit = 50,
): Promise<InvoiceHistoryEntry[]> {
  const res = await apiGet<{ data: InvoiceHistoryEntry[] }>(
    `/invoices/${id}/history?limit=${limit}`,
  );
  return res.data;
}

// MKS-214 — manual payment marking. `paidAt` omitted means today (resolved
// server-side in UTC, so two people in different timezones get the same day).
export async function markInvoicePaid(id: number, paidAt?: string): Promise<Invoice> {
  const res = await apiPost<{ data: Invoice }>(`/invoices/${id}/paid`, paidAt ? { paidAt } : {});
  return res.data;
}

export async function unmarkInvoicePaid(id: number): Promise<Invoice> {
  const res = await apiDelete<{ data: Invoice }>(`/invoices/${id}/paid`);
  return res.data;
}

// MKS-350 — the client-facing timesheet export. The three switches mirror the
// backend defaults exactly; they are always sent explicitly so what the person
// sees ticked is what the workbook contains.
export interface InvoiceTimesheetExportOptions {
  includeAmounts: boolean;
  includeTask: boolean;
  includeDescriptions: boolean;
}

export async function downloadInvoiceTimesheetXlsx(
  id: number,
  options: InvoiceTimesheetExportOptions
): Promise<{ blob: Blob; filename: string | null }> {
  const query = new URLSearchParams({
    includeAmounts: String(options.includeAmounts),
    includeTask: String(options.includeTask),
    includeDescriptions: String(options.includeDescriptions),
  });

  const response = await fetch(`${API_BASE_URL}/invoices/${id}/timesheet/xlsx?${query}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to download the timesheet: ${response.statusText}`);
  }

  // Prefer the server's own name — it carries the invoice name with the
  // diacritics intact in the RFC 5987 parameter.
  const disposition = response.headers.get('Content-Disposition');
  const utf8 = disposition?.match(/filename\*=UTF-8''([^;]+)/);
  const ascii = disposition?.match(/filename="?([^";]+)"?/);
  const filename = utf8?.[1] ? decodeURIComponent(utf8[1]) : (ascii?.[1] ?? null);

  return { blob: await response.blob(), filename };
}

// ===== MKS-334: the Receivables page =====

/** The overdue aging columns. `notDue` money has its own field, not a bucket. */
export interface AgingBucketAmounts {
  d1to30: number;
  d31to60: number;
  d61to90: number;
  d90plus: number;
}

/** The figures a client row and the totals row both carry. */
export interface ReceivablesAmounts {
  outstandingTotal: number;
  overdueTotal: number;
  notYetDueTotal: number;
  overdueInvoiceCount: number;
  invoiceCount: number;
  oldestDaysOverdue: number;
  buckets: AgingBucketAmounts;
}

/** One unpaid invoice inside an expanded client row. */
export interface ReceivablesInvoiceRow {
  id: number;
  invoiceNumber: string | null;
  name: string | null;
  projectName: string | null;
  /** The issue date — the invoice's creation day, NOT the billed period. */
  issueDate: string;
  /** The STORED due date; null on an invoice accounting never dated. */
  dueDate: string | null;
  /**
   * MKS-454 — what `daysOverdue` was measured from: `dueDate` when there is
   * one, else a date derived from the issue date and the projects' payment
   * term. Null while `mks_454_derived_invoice_due_date` is off.
   */
  effectiveDueDate: string | null;
  /** The term a derived date used, in days; null when nothing was derived. */
  derivedNetDays: number | null;
  daysOverdue: number;
  /** This client's share of the invoice, so the rows reconcile with the client total. */
  amount: number;
  bucket: keyof AgingBucketAmounts | 'notDue';
}

export interface ReceivablesClientRow extends ReceivablesAmounts {
  /** `c-<customerId>` or `none` — also the value of the client filter. */
  key: string;
  customerId: number | null;
  customerName: string;
  currency: string;
  invoices: ReceivablesInvoiceRow[];
}

export interface ReceivablesDso {
  windowDays: number;
  from: string;
  to: string;
  receivables: number;
  invoiced: number;
  /** null when nothing was invoiced in the window. */
  days: number | null;
}

export interface ReceivablesReport {
  rows: ReceivablesClientRow[];
  totals: ReceivablesAmounts;
  dso: ReceivablesDso[];
  currency: string;
  asOf: string;
}

/**
 * MKS-334 — the whole page in one request: the client rows (aging buckets and
 * the nested unpaid invoices included, so expanding a row needs no second
 * call), the totals row and the two DSO figures. Sorted server-side.
 */
export async function getReceivables(filters?: {
  client?: string;
  overdueOnly?: boolean;
}): Promise<ReceivablesReport> {
  const params = new URLSearchParams();
  if (filters?.client) params.set('client', filters.client);
  // The server defaults to overdue-only; only the "all unpaid" view says so.
  if (filters?.overdueOnly === false) params.set('overdueOnly', 'false');
  const query = params.toString();
  const res = await apiGet<{ data: ReceivablesReport }>(
    `/invoices/receivables${query ? `?${query}` : ''}`
  );
  return res.data;
}
